import { AxiosError } from 'axios';
import { getOrgInvites, getOrgMembersWithOwner } from '@nx-frontend/shared/api';
import { BillingAddon, OrgInvite, OrgMember, OrgMemberRole } from '@nx-frontend/shared/types';
import { logger } from '@nx-frontend/shared/utils';
import { getSubscriptionAddon } from './get-subscription-addon';
import { getUserPrices } from './get-user-prices';
import { getUserSubscriptions } from './get-user-subscriptions';

interface GetAddonPriceInfoParams {
  orgServiceUrl: string;
  billingServiceUrl: string;
  userId: number;
  orgId: string;
  addonCode: string;
  subscriptionId: string;
  /**
   * Secret for accessing internal services, not a super secret
   */
  secret: string;
  /**
   * Instance of addon with code addonCode, if it's already fetched to avoid fetching twice
   */
  addon?: BillingAddon;
  quantity?: number;
}

function getCurrentMembers(members: OrgMember[], invites: OrgInvite[]) {
  const currentMembers =
    members.filter((item) => item.role !== OrgMemberRole.GUEST && item.role !== OrgMemberRole.CLIENT).length +
    invites.filter((item) => item.role !== OrgMemberRole.GUEST).length;
  return currentMembers;
}

export interface AddonPriceInfo {
  /**
   * Price for one addon item
   */
  addonPrice: number;
  /**
   * Total price for addon items, depending on number of members for some addons
   */
  totalForAddon: number;
  /**
   * Total price for next period: subscription price + total price for addon items
   */
  totalForNextPeriod: number;
  /**
   * Price for now
   */
  payAmountNow: number;
  /**
   * Count of members, that are counted for addons
   */
  membersCount: number;
  /**
   *  Current subscription price
   */
  subscriptionTotal: number;
  /**
   * New addons quantity
   */
  quantity: number;
}

/**
 * Get addon price info
 * @param params
 * @returns
 */
export async function getAddonPriceInfo(params: GetAddonPriceInfoParams): Promise<AddonPriceInfo> {
  const { orgServiceUrl, billingServiceUrl, userId, orgId, addonCode, subscriptionId, secret } = params;

  const subscriptions = await getUserSubscriptions({
    host: billingServiceUrl,
    userId,
    secret,
  });

  const subscription = subscriptions.find((item) => item.uuid === subscriptionId);
  if (!subscription) {
    throw new Error(`Subscription ${subscriptionId} not found`);
  }

  const prices = await getUserPrices({
    host: billingServiceUrl,
    userId,
  });
  const members = await getOrgMembersWithOwner({
    host: orgServiceUrl,
    orgId,
    userId,
  });
  const invites = await getOrgInvites({
    host: orgServiceUrl,
    orgId,
    userId,
  });
  const membersCount = getCurrentMembers(members, invites);
  let quantity = membersCount;

  let addon;
  try {
    addon =
      params.addon ||
      (await getSubscriptionAddon({
        host: billingServiceUrl,
        userId,
        subscriptionId,
        addonCode,
        secret,
      }));
  } catch (err) {
    if (err instanceof AxiosError) {
      logger.debug(`Can't get subscription addon %j`, err?.response?.data);
    } else {
      logger.debug(`Can't get subscription addon %j`, err);
    }
  }

  if (addonCode === 'portals') {
    quantity = addon ? addon.quantity + 1 : 1;
  }

  if (addonCode === 'transcribe' && params.quantity) {
    quantity = params.quantity;
  }
  if (addonCode === 'automation' && params.quantity) {
    quantity = params.quantity;
  }

  const addonPrice = prices[`${addonCode}_addon_${subscription.plan.code}_en` as keyof typeof prices];
  let totalForAddon = membersCount * addonPrice;
  if (addonCode === 'portals') {
    totalForAddon = addonPrice;
  }
  if (addonCode === 'transcribe') {
    totalForAddon = addonPrice * quantity;
  }
  if (addonCode === 'automation') {
    totalForAddon = addonPrice * quantity;
  }

  const totalForNextPeriod = subscription.total + totalForAddon;
  const payAmountNow = 0;

  return {
    addonPrice,
    totalForAddon,
    totalForNextPeriod,
    payAmountNow,
    membersCount,
    subscriptionTotal: subscription.total,
    quantity,
  };
}
